/**
 * Shared chrome for the Admin section screens (`/admin/*`).
 *
 * Every admin screen renders inside AdminLayout — which owns the sidebar, the
 * breadcrumb, the sticky sub-tab strip AND the scroll container — so this shell
 * only owns the max-width column and the page head (icon tile / title /
 * description / actions), keeping the eight screens identical without repeating
 * the layout in each one.
 *
 * Deliberately NOT a scroll container: AdminLayout's main column scrolls, and a
 * second scroller here would trap the page inside it — the sticky tab strip
 * would never move and the screens' own sticky rails would resolve against the
 * wrong element.
 */
import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";

import Flx from "../../../components/layout/Flx";
import Txt from "../../../components/typography/Txt";
import { A, ADMIN_MAX_W } from "./adminTokens";

export const AdminScreenShell = ({
  icon,
  title,
  count,
  subtitle,
  actions,
  loading,
  maxWidth = ADMIN_MAX_W,
  // Page background behind the column; the admin gray unless a screen asks.
  background = A.bg,
  // Prose is capped for readability; pass "none" on a screen whose description
  // is a single line that should run the width of the column.
  subtitleMaxWidth = 760,
  // A chip beside the title (e.g. "Proof of concept").
  titleBadge,
  // The page fills the viewport instead of growing with its content: the
  // column becomes a flex column and the content below the head takes the
  // remaining height (a board whose lanes run to the bottom of the screen).
  fillHeight = false,
  // Inset around the head and content; a dense screen (a full-bleed board)
  // passes a smaller one. Defaults to the admin screens' standard inset.
  padding,
  // Replaces the title text (e.g. title-sized tabs); the badge still follows it.
  heading,
  children,
}) => (
  <Box
    sx={{
      bgcolor: background,
      minHeight: "100%",
      ...(fillHeight && { height: "100%", display: "flex", flexDirection: "column" }),
    }}
  >
    <Box
      sx={{
        maxWidth,
        mx: "auto",
        p: padding ?? { xs: 2, md: 3 },
        pb: padding ?? (fillHeight ? { xs: 2, md: 3 } : { xs: 8, md: 10 }),
        ...(fillHeight && {
          width: "100%",
          flex: 1,
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
        }),
      }}
    >
      {/* Title and description stack in one column with the actions opposite
          them, so the description reads as a column of text beside the controls
          rather than running the full width underneath them. The row is
          top-aligned: the actions belong beside the title, not centred against a
          block whose height depends on how long the description is. */}
      <Flx jb g={2} wrap sx={{ alignItems: "flex-start", mb: 2, flexShrink: 0 }}>
        <Flx column g={0.75} sx={{ minWidth: 0, flex: 1 }}>
          <Flx ac g={1.25}>
            {/* The screen's glyph sits in a soft teal tile — the same treatment
                the sidebar's own mark gets, so the page reads as that item. */}
            {icon ? (
              <Flx
                center
                sx={{
                  width: 28,
                  height: 28,
                  flexShrink: 0,
                  borderRadius: "8px",
                  bgcolor: A.tealSoft,
                  color: A.tealDeep,
                  "& svg": { fontSize: 16, color: "inherit" },
                }}
              >
                {icon}
              </Flx>
            ) : null}
            {heading || (
              <Txt
                sx={{
                  fontSize: 20,
                  fontWeight: 700,
                  letterSpacing: "-.015em",
                  color: A.ink,
                }}
              >
                {title}
                {Number.isFinite(count) ? ` (${count})` : ""}
              </Txt>
            )}
            {titleBadge}
            {loading ? <CircularProgress size={16} thickness={5} /> : null}
          </Flx>
          {subtitle ? (
            <Txt
              sx={{
                fontSize: 12.5,
                color: A.muted,
                display: "block",
                lineHeight: 1.5,
                maxWidth: subtitleMaxWidth,
              }}
            >
              {subtitle}
            </Txt>
          ) : null}
        </Flx>
        {actions ? (
          <Flx ac g={1} wrap sx={{ flexShrink: 0 }}>
            {actions}
          </Flx>
        ) : null}
      </Flx>

      {children}
    </Box>
  </Box>
);

AdminScreenShell.propTypes = {
  icon: PropTypes.node,
  title: PropTypes.string.isRequired,
  count: PropTypes.number,
  subtitle: PropTypes.node,
  actions: PropTypes.node,
  loading: PropTypes.bool,
  // A number caps the column; "none" lets a screen run the full width.
  maxWidth: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  background: PropTypes.string,
  titleBadge: PropTypes.node,
  fillHeight: PropTypes.bool,
  heading: PropTypes.node,
  padding: PropTypes.oneOfType([PropTypes.number, PropTypes.string, PropTypes.object]),
  subtitleMaxWidth: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  children: PropTypes.node,
};

/** A plain white card — the default container for a section of an admin page. */
export const AdminCard = ({ title, endContent, dense, children, sx }) => (
  <Box
    sx={{
      bgcolor: A.surface,
      border: `1px solid ${A.line}`,
      borderRadius: "12px",
      boxShadow: A.shadowSm,
      mb: 2,
      transition: "box-shadow .18s",
      "&:hover": { boxShadow: A.shadowMd },
      ...sx,
    }}
  >
    {title || endContent ? (
      <Flx
        ac
        jb
        g={1}
        sx={{
          px: 2,
          py: 1.25,
          borderBottom: "1px solid #eef2f6",
        }}
      >
        <Txt sx={{ fontSize: 14, fontWeight: 600, color: "#0f172a" }}>
          {title}
        </Txt>
        {endContent}
      </Flx>
    ) : null}
    <Box sx={{ p: dense ? 0 : 2 }}>{children}</Box>
  </Box>
);

AdminCard.propTypes = {
  title: PropTypes.node,
  endContent: PropTypes.node,
  dense: PropTypes.bool,
  children: PropTypes.node,
  sx: PropTypes.object,
};

/** Empty / error placeholder inside a card. */
export const AdminEmpty = ({ children }) => (
  <Txt
    sx={{
      fontSize: 12.5,
      color: "#94a3b8",
      display: "block",
      textAlign: "center",
      py: 3,
    }}
  >
    {children}
  </Txt>
);

AdminEmpty.propTypes = { children: PropTypes.node };

export default AdminScreenShell;
